import React from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, PagingSidePanelType, RowsSkeletonLoader, SidePanel } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { DateFormat, PathNames } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useGetMassFiatPayoutsDetailsQuery } from 'hooks/queries/MassPayouts/useGetMassFiatPayoutsDetails'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { getSumOfRecords, getValidRecords } from 'pages/MassPayouts/utils'
import { WalletType, WalletTypes } from 'types/types'
import { highlightRowBasedOnId } from 'utils/table/table'
import { FiatPayout } from '../../types'
import { MassPayoutListSidePanelBody } from '../MassPayoutListSidePanelBody'

export type MassPayoutListSummaryProps = {
  isOpen: boolean
  massFiatPayoutDetails: FiatPayout
  pageMassPayout: PagingSidePanelType
  onClose: () => void
  wallets: WalletType[]
  isLoadingWallets: boolean
}

export type SidePanelBodyDataProps = {
  massPayoutDetails: FiatPayout
  country: string
}

export const FiatBatchSidePanel = ({
  isOpen,
  massFiatPayoutDetails,
  pageMassPayout,
  onClose,
  wallets,
  isLoadingWallets,
}: MassPayoutListSummaryProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { data: payoutDetails, isLoading: isLoadingMassFiatPayoutDetails } =
    useGetMassFiatPayoutsDetailsQuery(massFiatPayoutDetails?.reference)

  const selectedWallet = wallets?.find(wallet => wallet?.lsid === payoutDetails?.walletId)
  const walletName = `${selectedWallet?.description ?? massFiatPayoutDetails?.walletId} (${selectedWallet?.currency?.code})`

  const primaryLabel = t('massPayouts.numberOfPayoutsProcessed', {
    numberOfProcessedPayments: getValidRecords(payoutDetails?.payoutStatuses),
    numberOfPayments: getSumOfRecords(payoutDetails?.payoutStatuses),
  })
  const secondaryLabel = payoutDetails?.alias

  const uploadedBy = payoutDetails?.createdBy?.fullName

  const date = dayjs(payoutDetails?.createdAt).format(DateFormat.DD_MMMM_YYYY)

  const handleViewBatchTransaction = () => {
    if (massFiatPayoutDetails?.reference) {
      navigate(
        `${PathNames.MASS_PAYOUTS}/payout-details/${massFiatPayoutDetails.reference}?batchType=${WalletTypes.FIAT}`
      )
    }
  }

  return (
    <SidePanel
      id={'side-panel-fiat-batch'}
      isOpen={isOpen}
      paging={pageMassPayout}
      onClose={onClose}
      activeRowId={massFiatPayoutDetails?.reference}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        isLoadingMassFiatPayoutDetails ? (
          <RowsSkeletonLoader rows={1} />
        ) : (
          <SidePanelHeaderTemplateTwo
            heading={{
              iconUrls: null,
              primaryLabel: (
                <Box className="md:flex w-full">
                  <h2 className="text-h2 text-foreground-prominent">{primaryLabel}</h2>
                </Box>
              ),
              secondaryLabel: (
                <p className="text-body-regular text-foreground-subdued">{secondaryLabel}</p>
              ),
            }}
            bottomContent={{
              leftLabel: t('massPayouts.dateCreated'),
              leftDescription: date && (
                <p className="text-body-regular text-foreground-faint mb-1 flex-wrap">{date}</p>
              ),
              rightLabel: t('massPayouts.uploadedBy'),
              rightDescription: uploadedBy && (
                <p className="text-body-regular text-foreground-faint mb-1 flex-wrap break-words">
                  {uploadedBy}
                </p>
              ),
            }}
          />
        )
      }
      body={
        isLoadingMassFiatPayoutDetails || isLoadingWallets ? (
          <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
        ) : (
          <Box width="full" background="standard">
            <MassPayoutListSidePanelBody
              dateCreated={date}
              uploadedBy={uploadedBy}
              paidFromWallet={walletName}
              batchReference={massFiatPayoutDetails?.reference}
              batchStatus={massFiatPayoutDetails?.status}
              onViewBatchTransaction={handleViewBatchTransaction}
            />
          </Box>
        )
      }
    />
  )
}
